import { useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { X, UserSwitch, XCircle } from "@phosphor-icons/react";
import { LEAD_STATUSES, LOST_REASONS } from "./LeadCells";

export const emptyFilters = { status: "all", source: "all", assigned: "all", from: "", to: "", no: "" };

export function matchesFilters(lead, f) {
  const status = (lead.status || "new").toLowerCase();
  if (f.status !== "all" && status !== f.status) return false;
  if (f.source !== "all" && (lead.source || "").trim() !== f.source) return false;
  if (f.assigned !== "all") {
    const who = (lead.salesperson || "").trim();
    if (f.assigned === "__none" ? who !== "" : who.toLowerCase() !== f.assigned.toLowerCase()) return false;
  }
  // SALES-041: the number the B squad texts use ("#3", "3").
  const no = String(f.no || "").replace(/\D/g, "");
  if (no && String(lead.lead_no || "") !== no) return false;
  const day = String(lead.created_at || "").slice(0, 10);
  if (f.from && day && day < f.from) return false;
  if (f.to && day && day > f.to) return false;
  return true;
}

export function LeadsFilterBar({ filters, setFilters, sources, team, selected, clearSelection, onDone }) {
  const [busy, setBusy] = useState(false);
  const [assignTo, setAssignTo] = useState("");
  const [reason, setReason] = useState(LOST_REASONS[0]);
  const n = selected.length;

  const run = async (action, extra) => {
    setBusy(true);
    try {
      const { data } = await api.post("/leads/bulk", { ids: selected, action, ...extra });
      toast.success(`${data.updated} lead${data.updated === 1 ? "" : "s"} updated`);
      clearSelection();
      onDone?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="leads-filter-bar">
      <div className="flex flex-wrap items-end gap-3 border border-border bg-card p-4">
        <div>
          <div className="label-mono mb-1.5">Lead #</div>
          <Input inputMode="numeric" placeholder="e.g. 3" value={filters.no || ""} onChange={(e) => setFilters({ ...filters, no: e.target.value })}
            className="bg-input border-border w-24 h-9" aria-label="Lead number from the B squad texts" data-testid="leads-filter-no" />
        </div>
        <div>
          <div className="label-mono mb-1.5">Status</div>
          <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
            <SelectTrigger className="bg-input border-border w-36 h-9" data-testid="leads-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {LEAD_STATUSES.map((s) => <SelectItem key={s} value={s}>{s.toUpperCase()}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="label-mono mb-1.5">Source</div>
          <Select value={filters.source} onValueChange={(v) => setFilters({ ...filters, source: v })}>
            <SelectTrigger className="bg-input border-border w-44 h-9" data-testid="leads-filter-source"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All sources</SelectItem>
              {sources.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="label-mono mb-1.5">Assigned to</div>
          <Select value={filters.assigned} onValueChange={(v) => setFilters({ ...filters, assigned: v })}>
            <SelectTrigger className="bg-input border-border w-40 h-9" data-testid="leads-filter-assigned"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Anyone</SelectItem>
              <SelectItem value="__none">Unassigned</SelectItem>
              {team.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <div className="label-mono mb-1.5">Came in from</div>
          <Input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} className="bg-input border-border w-44 h-9" data-testid="leads-filter-from" />
        </div>
        <div>
          <div className="label-mono mb-1.5">To</div>
          <Input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} className="bg-input border-border w-44 h-9" data-testid="leads-filter-to" />
        </div>
        <Button variant="ghost" onClick={() => setFilters(emptyFilters)} className="h-9" data-testid="leads-filter-reset">
          <X size={13} className="mr-1" />Reset
        </Button>
      </div>

      {n > 0 && (
        <div className="hidden md:flex flex-wrap items-center gap-3 border border-primary/40 bg-primary/[0.05] px-4 py-3" data-testid="leads-bulk-bar">
          <span className="label-mono text-primary">{n} selected</span>
          <div className="flex items-center gap-2">
            <Select value={assignTo} onValueChange={setAssignTo}>
              <SelectTrigger className="bg-input border-border w-44 h-9" data-testid="leads-bulk-assign-select"><SelectValue placeholder="Assign to…" /></SelectTrigger>
              <SelectContent>{team.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" disabled={busy || !assignTo} onClick={() => run("assign", { salesperson: assignTo })} className="bg-primary text-primary-foreground hover:bg-primary/90 h-9" data-testid="leads-bulk-assign">
              <UserSwitch size={14} className="mr-1.5" />Assign
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger className="bg-input border-border w-48 h-9" data-testid="leads-bulk-reason"><SelectValue /></SelectTrigger>
              <SelectContent>{LOST_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" variant="outline" disabled={busy} onClick={() => run("lost", { lost_reason: reason })} className="h-9 border-red-500/40 text-red-400 hover:bg-red-500/10" data-testid="leads-bulk-lost">
              <XCircle size={14} className="mr-1.5" />Mark as lost
            </Button>
          </div>
          <Button size="sm" variant="ghost" onClick={clearSelection} className="h-9 ml-auto" data-testid="leads-bulk-clear">Clear</Button>
        </div>
      )}
    </div>
  );
}
